import moment from 'moment';
import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import RemotePage from '../components/Remote/RemotePage';
import UserPrompt from '../components/UserPrompt';
import { OPEN_USER_PROMPT } from '../actions/actionTypes';
import {
  initialDevice,
  enterRoom,
  sendDataCurrentTarget,
  sendFileDataCurrentTarget,
  exitRoom,
} from '../lib/remote';
import {
  USER_PROMPT_MODE,
  REMOTE_MEMBER_TYPE,
  REMOTE_MEMBER_STATUS,
  REMOTE_COMMAND,
  TLVX_COMMANDS,
} from '../lib/constants';
import {
  createRoom,
  getEnterRoomInfo,
  getRoomInfo,
  updateMemberStatus,
  getBlackboard,
  createBlackboard,
  recallRoomMember,
} from '../actions/api/apis';
import getOperators from '../actions/api/operators/getOperators';
import { REMOTE_SUPPORT_ERROR_CODE } from '../lib/apiErrorCodes';
import { consoleNormalLog } from '../lib/tools';

const INTERVAL_TIMER = 10000;
/** 初期状態 */
const STEP0 = 0;
/** デバイス準備OK、ルーム作成APIもしくは入室情報取得APIコール処理をする */
const STEP1 = 1;
/** ルーム作成成功時、SDKに対して入室を行う */
const STEP2 = 2;
/** 入室処理後、チャット中 */
const STEP3 = 3;
/** 他メンバーが全て退室済みもしくは拒否状態で、ルーム削除依頼済み */
// const STEP97 = 97;
/** ルーム作成から3分経過し、入室状態のメンバーが他に誰もいない状態でルーム削除依頼済み */
const STEP98 = 98;
/** デバイス準備NG */
const STEP99 = 99;
const DEDICATED_MESSAGE_ERROR_CODE = [
  REMOTE_SUPPORT_ERROR_CODE.remoteSupportNow,
  REMOTE_SUPPORT_ERROR_CODE.noLicense,
  REMOTE_SUPPORT_ERROR_CODE.licenseExceeded,
  REMOTE_SUPPORT_ERROR_CODE.clientLeft,
];

const updateContainer = (roomEntranceInfo, apiToken, dispatch) => {
  const appRoomId = (roomEntranceInfo && roomEntranceInfo.appRoomId) || null;
  if (appRoomId && dispatch && apiToken) {
    dispatch(getRoomInfo(apiToken, appRoomId));
  }
};

const isClientLeft = errorCode =>
  errorCode === REMOTE_SUPPORT_ERROR_CODE.clientLeft;
const createMessage = errorCode => {
  const id = DEDICATED_MESSAGE_ERROR_CODE.includes(errorCode)
    ? `remotePage.message.disconnectMessage.${errorCode}`
    : 'remotePage.message.somthingError';
  return { id, values: { errorCode }, parse: true };
};
const createButton = (isClientLeftError, normalSupport) => {
  const type = 0;
  /** 遠隔支援タブを閉じるボタン */
  const buttonClose = {
    name: 'userPrompt.button.close',
    type,
  };
  /** ダイアログのOKボタン */
  const buttonOk = {
    name: 'userPrompt.button.ok',
    type,
  };
  const result = (isClientLeftError && [buttonClose]) ||
    (normalSupport && [buttonClose]) || [buttonOk];
  return result;
};
const createFunction = (dispatch, isClientLeftError, normalSupport) => {
  const closeRemoteWindow = ret => {
    // ボタン配列のindexと一致させる
    if (ret === 0) {
      window.close();
    }
    dispatch({ type: 'INIT_ROOM_ERROR_CODE' });
  };
  const resetRoomErrorCode = () => dispatch({ type: 'INIT_ROOM_ERROR_CODE' });
  return (
    (isClientLeftError && closeRemoteWindow) ||
    (normalSupport && closeRemoteWindow) ||
    resetRoomErrorCode
  );
};
class RemoteContainer extends Component {
  constructor(props) {
    super(props);
    props.dispatch({ type: 'SET_STEP', step: STEP0 });
  }

  componentDidMount() {
    // デバイス初期化
    const { dispatch } = this.props;
    initialDevice(2, result => {
      dispatch({ type: 'REMOTE_SUPPORT_READY', result });
      if (result) {
        dispatch({ type: 'SET_STEP', step: STEP1 });
      } else {
        dispatch({ type: 'SET_STEP', step: STEP99 });
      }
    });
  }

  shouldComponentUpdate(props) {
    const {
      dispatch,
      apiToken,
      canEnterRoom,
      roomEntranceInfo,
      remoteSupportReady,
      showUserPrompt,
      resetRequest,
      errorCode,
      step,
      roomMembers,
      userInfo,
      roomDeleted,
      oppositeOperatorIds,
      oppositeDeviceId,
      blackboardId,
      favoriteId,
      targetGroupId,
      calledGetRoomInfo,
    } = props;
    const { match } = props;
    const params = { match, apiToken, dispatch };
    const command = params.match.params.command || null;
    // 通常支援かどうか(遠隔支援開始時に対デバイスIDが設定されている場合は通常支援)
    const normalSupport = !!oppositeDeviceId;
    if (errorCode) {
      // getRoomでエラーが発生した場合はここでメッセージを表示する
      const isClientLeftError = isClientLeft(errorCode);
      if (isClientLeftError && !roomDeleted) {
        exitRoom();
      }
      // エラーとダイアログ表示する
      const promptMessage = createMessage(errorCode);
      const buttonArray = createButton(isClientLeftError, normalSupport);
      const onAccept = createFunction(
        dispatch,
        isClientLeftError,
        normalSupport,
      );
      const mode = USER_PROMPT_MODE.requiredConvert;
      // userPromptのボタンの位置を変更する場合はonAcceptは二つ用意すること
      dispatch({
        type: OPEN_USER_PROMPT,
        promptMessage,
        buttonArray,
        onAccept,
        mode,
      });
    }
    switch (step) {
      case STEP1: {
        consoleNormalLog(`[**STEP1:createRoom/getEnterRoomInfo**]${command}`);
        // デバイスの初期化に成功したのでcreateRoom/getEnterRoomInfoを実行
        dispatch({ type: 'SET_REMOTE_ENABLED' });
        switch (command) {
          case REMOTE_COMMAND.caller: {
            if (oppositeOperatorIds.length > 0) {
              const createRoomParams = !favoriteId
                ? {
                    oppositeOperatorIds,
                    oppositeDeviceId: oppositeDeviceId || undefined,
                    memberType: REMOTE_MEMBER_TYPE.operator,
                    groupId: targetGroupId || undefined,
                  }
                : {
                    favoriteId: favoriteId || undefined,
                    memberType: REMOTE_MEMBER_TYPE.operator,
                    groupId: targetGroupId || undefined,
                  };
              dispatch(createRoom(apiToken, createRoomParams));
              dispatch(getOperators(apiToken, { ids: oppositeOperatorIds }));
              // 黒板IDが指定されている場合は黒板情報も取得する
              if (blackboardId) {
                dispatch(getBlackboard(apiToken, blackboardId));
              }
            }
            break;
          }
          default: {
            const { appRoomId } = roomEntranceInfo; // preparationContainerから設定した情報
            dispatch(
              getEnterRoomInfo(apiToken, { appRoomId, groupId: targetGroupId }),
            );
          }
        }
        dispatch({ type: 'SET_STEP', step: STEP2 });
        break;
      }
      case STEP2: {
        const p = {
          roomEntranceInfo,
          apiToken,
          dispatch,
          showUserPrompt,
          resetRequest,
        };
        if (roomEntranceInfo && roomEntranceInfo.roomId && canEnterRoom) {
          if (resetRequest) {
            const { appRoomId } = roomEntranceInfo;
            dispatch(updateMemberStatus(apiToken, appRoomId));
          }
          if (remoteSupportReady) {
            // createRoomに成功したのでenterRoom
            const clientInfo = { normalSupport };
            enterRoom(dispatch, roomEntranceInfo, clientInfo);
            dispatch({ type: 'ENTER_ROOM' });
            if (showUserPrompt) {
              this.closeTimer();
            } else {
              this.initialTimer(p);
              dispatch({ type: 'SET_STEP', step: STEP3 });
            }
          }
        }
        break;
      }
      case STEP3: {
        // ルームメンバー取得前に削除処理が走らないようにする
        if (calledGetRoomInfo) {
          const { operatorId } = userInfo;
          const otherMembers = roomMembers.filter(
            member => member.operatorId !== operatorId,
          );
          const otherMemberJoined = otherMembers.filter(
            member =>
              member.status === REMOTE_MEMBER_STATUS.joined ||
              member.status === REMOTE_MEMBER_STATUS.joining,
          );
          const isTimeout = moment().isAfter(
            moment(roomEntranceInfo.roomCreatedAt).add(3, 'm'),
          );
          // ルーム作成から3分以上経過している場合
          if (otherMemberJoined.length === 0 && isTimeout) {
            dispatch({ type: 'SET_STEP', step: STEP98 });
          }
        }
        break;
      }

      // case STEP97: {
      //   consoleNormalLog('[**STEP97:request reject or other member left, close window **]');
      //   // window.close or reloadのためroomDeletedの初期化処理無し
      //   if (roomDeleted) {
      //     const promptMessage = 'remotePage.message.RoomEmpty';
      //     const buttonArray = [
      //       { type: 0, name: 'userPrompt.button.close' },
      //     ];
      //     const onAccept = (ret) => {
      //       if (ret === 0) {
      //         window.close();
      //       }
      //     };
      //     const mode = USER_PROMPT_MODE.requiredConvert;
      //     dispatch({ type: OPEN_USER_PROMPT, promptMessage, buttonArray, onAccept, mode });
      //   }
      //   break;
      // }
      case STEP98: {
        consoleNormalLog('[**STEP98:timeout, window.close or reload**]');
        // window.close or reloadのためroomDeletedの初期化処理無し
        if (roomDeleted) {
          // ルーム削除済みの場合は遠隔支援タブを閉じるか、
          // リロードし保存済み情報を利用してルーム再作成を行うことで遠隔支援を再び開始する
          const promptMessage = 'remotePage.message.RequestTimeout';
          const buttonArray = [
            { type: 0, name: 'userPrompt.button.close' },
            { type: 1, name: 'userPrompt.button.reconnect' },
          ];
          const onAccept = ret => {
            if (ret === 0) {
              window.close();
            }
            if (ret === 1) {
              window.location.reload();
            }
          };
          const mode = USER_PROMPT_MODE.requiredConvert;
          dispatch({
            type: OPEN_USER_PROMPT,
            promptMessage,
            buttonArray,
            onAccept,
            mode,
          });
        }
        break;
      }
      case STEP99: {
        consoleNormalLog('[**STEP99:device error**]');
        // デバイスの初期化に失敗
        const promptMessage = 'remotePage.message.deviceNotReady.2';
        const buttonArray = createButton(false, normalSupport);
        const onAccept = createFunction(dispatch, false, normalSupport);
        const mode = USER_PROMPT_MODE.requiredConvert;
        dispatch({
          type: OPEN_USER_PROMPT,
          promptMessage,
          buttonArray,
          onAccept,
          mode,
        });
        break;
      }
      default: {
        // デバイスの準備前なので無視
        break;
      }
    }
    return true;
  }

  initialTimer(params) {
    const { dispatch, apiToken, showUserPrompt, roomEntranceInfo } = params;
    this.closeTimer();
    if (dispatch && apiToken && !showUserPrompt) {
      updateContainer(roomEntranceInfo, apiToken, dispatch);
      this.mainTimer = setInterval(
        () => updateContainer(roomEntranceInfo, apiToken, dispatch),
        INTERVAL_TIMER,
      );
    }
  }

  closeTimer() {
    if (this.mainTimer !== undefined) {
      clearInterval(this.mainTimer);
      delete this.mainTimer;
    }
  }

  render() {
    const {
      locale,
      userInfo,
      isLogin,
      isWaiting,
      dispatch,
      isSticker,
      isPaint,
      isSound,
      isRemoteControl,
      isBlackboard,
      isLight,
      isPause,
      roomEntranceInfo,
      roomMembers,
      apiToken,
      isVideo,
      incomingParticipantIds,
      outgoingParticipantIds,
      sendTextInfo,
      blackboardInfo,
      currentSharer,
      oppositeDeviceId,
    } = this.props;
    const normalSupport = !!oppositeDeviceId;
    return (
      (!isLogin && <Redirect to="/login" />) || (
        <Fragment key="RemotePageTop">
          <UserPrompt />
          <RemotePage
            locale={locale}
            userInfo={userInfo}
            roomMembers={roomMembers}
            roomEntranceInfo={roomEntranceInfo}
            toggleRemoteControl={() =>
              dispatch({ type: 'TOGGLE_REMOTE_CONTROL' })
            }
            toggleSticker={() => dispatch({ type: 'TOGGLE_STICKER' })}
            togglePaint={() => dispatch({ type: 'TOGGLE_PAINT' })}
            toggleSound={() => dispatch({ type: 'TOGGLE_SOUND' })}
            toggleVideo={() => dispatch({ type: 'TOGGLE_TRANS_MODE' })}
            toggleBlackboard={() => dispatch({ type: 'TOGGLE_BLACKBOARD' })}
            toggleLight={() => dispatch({ type: 'TOGGLE_LIGHT' })}
            togglePause={() => dispatch({ type: 'TOGGLE_PAUSE' })}
            isWaiting={isWaiting}
            isSticker={isSticker}
            isPaint={isPaint}
            isSound={isSound}
            isRemoteControl={isRemoteControl}
            isBlackboard={isBlackboard}
            isVideo={isVideo}
            isLight={isLight}
            isPause={isPause}
            sendTextInfo={sendTextInfo}
            onSendShow={isShow => dispatch({ type: 'SEND_SHOW', isShow })}
            onSendColor={key => dispatch({ type: 'SEND_COLOR', key })}
            onSendOpacity={value => dispatch({ type: 'SEND_OPACITY', value })}
            onSendPosition={key => dispatch({ type: 'SEND_POSITION', key })}
            onSendPhoto={() => dispatch({ type: 'SEND_PHOTO' })}
            onUpdateText={params =>
              dispatch({ type: 'UPDATE_REMOTE_TEXT', params })
            }
            onCreateBlackboard={params =>
              dispatch(createBlackboard(apiToken, params))
            }
            onSendBlackboardText={() =>
              dispatch({ type: 'SEND_REMOTE_BLACKBOARD_TEXT' })
            }
            expand={() => dispatch({ type: 'EXPAND_REMOTE_SCREEN' })}
            contract={() => dispatch({ type: 'CONTRACT_REMOTE_SCREEN' })}
            resetZoom={() => dispatch({ type: 'RESET_REMOTE_SCREEN' })}
            blackboardInfo={blackboardInfo}
            shareVideo={participantId =>
              dispatch({ type: 'SHARE_REMOTE_CAMERA', participantId })
            }
            callAgain={params => dispatch(recallRoomMember(apiToken, params))}
            currentSharer={currentSharer}
            normalSupport={normalSupport}
            incomingParticipantIds={incomingParticipantIds}
            outgoingParticipantIds={outgoingParticipantIds}
            onSendChatData={async ({ chatData, files }) => {
              // チャットコマンド、チャット開始コマンドを送信する
              const sendTextCommand =
                files.length > 0
                  ? TLVX_COMMANDS.sendChatAndFileData
                  : TLVX_COMMANDS.sendChatData;
              sendDataCurrentTarget(sendTextCommand, chatData);

              if (files && files.length > 0) {
                try {
                  files.forEach(async (file, index) => {
                    await sendFileDataCurrentTarget(file);
                    if (index === files.length - 1) {
                      // チャット終了コマンドを送信する
                      sendDataCurrentTarget(
                        TLVX_COMMANDS.sendChatAndFileDataEnd,
                      );
                    }
                  });
                } catch (e) {
                  consoleNormalLog(
                    `[**ERROR:RemoteContainer**]onSendChatData:${e}`,
                  );
                }
              }
            }}
          />
        </Fragment>
      )
    );
  }
}

RemoteContainer.propTypes = {
  locale: PropTypes.string.isRequired,
  match: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  roomEntranceInfo: PropTypes.object.isRequired,
  roomMembers: PropTypes.array.isRequired,
  resetRequest: PropTypes.bool.isRequired,
  apiToken: PropTypes.string.isRequired,
  isLogin: PropTypes.bool.isRequired,
  isWaiting: PropTypes.bool.isRequired,
  isSticker: PropTypes.bool.isRequired,
  isPaint: PropTypes.bool.isRequired,
  isSound: PropTypes.bool.isRequired,
  isRemoteControl: PropTypes.bool.isRequired,
  isBlackboard: PropTypes.bool.isRequired,
  isLight: PropTypes.bool.isRequired,
  isPause: PropTypes.bool.isRequired,
  sendTextInfo: PropTypes.object.isRequired,
  canEnterRoom: PropTypes.bool.isRequired,
  remoteSupportReady: PropTypes.bool.isRequired,
  showUserPrompt: PropTypes.bool.isRequired,
  errorCode: PropTypes.string.isRequired,
  step: PropTypes.number.isRequired,
  isVideo: PropTypes.bool.isRequired,
  userInfo: PropTypes.object.isRequired,
  roomDeleted: PropTypes.bool.isRequired,
  incomingParticipantIds: PropTypes.array.isRequired,
  outgoingParticipantIds: PropTypes.array.isRequired,
  blackboardInfo: PropTypes.object.isRequired,
  oppositeOperatorIds: PropTypes.array.isRequired,
  blackboardId: PropTypes.number.isRequired,
  favoriteId: PropTypes.number.isRequired,
  oppositeDeviceId: PropTypes.number.isRequired,
  currentSharer: PropTypes.object.isRequired,
  targetGroupId: PropTypes.string.isRequired,
  calledGetRoomInfo: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  locale: state.main.locale,
  targetRoomId: state.main.targetRoomId,
  roomEntranceInfo: state.room.roomEntranceInfo,
  roomMembers: state.room.roomMembers,
  resetRequest: state.room.resetRequest,
  apiToken: state.main.apiToken,
  isLogin: state.main.isLogin,
  isWaiting: state.remote.isWaiting,
  isSticker: state.remote.isSticker,
  isPaint: state.remote.isPaint,
  isSound: state.remote.isSound,
  isRemoteControl: state.remote.isRemoteControl,
  isBlackboard: state.remote.isBlackboard,
  isLight: state.remote.isLight,
  isPause: state.remote.isPause,
  sendTextInfo: state.remote.sendTextInfo,
  canEnterRoom: state.room.canEnterRoom,
  remoteSupportReady: state.room.remoteSupportReady,
  showUserPrompt: state.userPrompt.showUserPrompt,
  errorCode: state.room.errorCode,
  step: state.room.step,
  isVideo: state.remote.isVideo,
  userInfo: state.main.userInfo,
  roomDeleted: state.room.roomDeleted,
  incomingParticipantIds: state.room.incomingParticipantIds,
  outgoingParticipantIds: state.room.outgoingParticipantIds,
  blackboardInfo: state.remote.blackboardInfo,
  oppositeOperatorIds: state.room.oppositeOperatorIds,
  blackboardId: state.room.blackboardId,
  favoriteId: state.room.favoriteId,
  oppositeDeviceId: state.room.oppositeDeviceId,
  currentSharer: state.room.currentSharer,
  targetGroupId: state.main.targetGroupId,
  calledGetRoomInfo: state.room.calledGetRoomInfo,
});

export default withRouter(connect(mapStateToProps)(RemoteContainer));
